import React, { useEffect, useState } from 'react';
import TableComponent from 'components/table-component/Table';
import Error from 'components/Error';
import { useTranslation } from 'react-i18next';
import { formsSetsHeaders } from '../utils';
import AlertModal from 'components/common-components/AlertModal';
import toast from 'react-hot-toast';
import { CallAPI } from 'actions/General';
import { isPermissionAvailable } from 'config/permissionUtils';
import { permissionKeys } from 'config/config';
import FormButton from 'components/form-components/FormButton';
import plusIcon from 'assets/images/icons/plus.svg';
import FormsSetsListHeaders from './FormsSetsListHeaders';
import AddOptionsSetsModal from './AddOptionsSetsModal';
import ClientPagination from '../ClientPagination';

const FormsSets = () => {
  const { t } = useTranslation();

  const [formsSets, setFormsSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inputFilters, setInputFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [APIError, setAPIError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [paginatedData, setPaginatedData] = useState([]);

  const handleFilterChange = (name, value) => {
    setInputFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDebouncedSearch = (name, value) => {
    setAppliedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddFormsSets = () => {
    setInputFilters({});
    setShowAddModal(true);
  };

  const handleCloseEditModal = () => {
    setSelectedRecordId(null);
    setShowAddModal(false);
  };

  const handleDeleteRecord = async () => {
    const result = await CallAPI(
      'DELETE_FORMS_SET',
      null,
      selectedRecordId,
      setDeleteLoading,
      setAPIError,
    );
    if (result.status) {
      toast.success(t('alert_success_form_set_deleted'));
      fetchFormsSets();
    }
    setShowDeleteConfirm(false);
  };

  const editDeleteFormsSetClick = (id, action) => {
    if (action === 'edit') {
      setSelectedRecordId(id);
      setShowAddModal(true);
    } else if (action === 'delete') {
      setSelectedRecordId(id);
      setShowDeleteConfirm(true);
    }
  };

  const fetchFormsSets = async () => {
    setSelectedRecordId(null);
    const result = await CallAPI(
      'GET_ALL_FORMS_SETS',
      { searchText: appliedFilters?.search ?? '' },
      null,
      setLoading,
      setAPIError,
    );
    if (result.status) {
      result.data.forEach((value, index) => {
        value.indexed = index + 1;
      });
      setFormsSets(result.data);
    }
  };

  useEffect(() => {
    fetchFormsSets();
  }, [appliedFilters]);

  return (
    <div>
      {APIError && <Error msg={APIError} />}

      <FormsSetsListHeaders
        filteringData={inputFilters}
        setFilteringData={handleFilterChange}
        debounceFunc={handleDebouncedSearch}
        handleAddFormsSets={handleAddFormsSets}
      />

      <TableComponent
        header={formsSetsHeaders}
        data={paginatedData}
        props={{
          editDeleteFormsSetClick: editDeleteFormsSetClick,
        }}
        totalRecords={0}
        loader={loading}
        filterEnabled={false}
        isLoading={loading}
        isFieldSelectionEnabled={false}
        isPaginationEnabled={false}
        isActionsEnabled={false}
        noDataComponent={
          <div className='lh-1 w-100 d-flex flex-column gap-2 justify-content-center align-items-center'>
            <span className='theme-text-black-1 theme-size-1_3'>
              {t('text_you_dont_have_any_form_meta_data')}
            </span>
            {isPermissionAvailable(permissionKeys.field_work_response_set_create) && (
              <>
                <span>{t('text_add_meta_data_to_get_started')}</span>
                <FormButton
                  text='button_add_new'
                  variant='green-1'
                  onClick={handleAddFormsSets}
                  icon={plusIcon}
                  iconClasses='svg-white'
                />
              </>
            )}
          </div>
        }
      />

      {formsSets.length > 0 && (
        <div className='p-3'>
          <ClientPagination
            data={formsSets}
            onChange={({ paginatedDataList }) => {
              setPaginatedData(paginatedDataList);
            }}
          />
        </div>
      )}

      {showAddModal && (
        <AddOptionsSetsModal
          show={showAddModal}
          editId={selectedRecordId}
          onCancel={handleCloseEditModal}
          refreshList={fetchFormsSets}
        />
      )}

      <AlertModal
        show={showDeleteConfirm}
        showCloseButton={true}
        closeModal={() => setShowDeleteConfirm(false)}
        variant='delete'
        message={'alert_delete_form_set'}
        actionButtons={[
          {
            text: t('button_confirm'),
            variant: 'red-1',
            onClick: () => {
              handleDeleteRecord();
            },
            loading: deleteLoading,
          },
          {
            text: t('button_cancel'),
            variant: 'white-1',
            onClick: () => {
              setShowDeleteConfirm(false);
              setSelectedRecordId(null);
            },
          },
        ]}
      />
    </div>
  );
};

export default FormsSets;
